/**
 * Renders a PREBUILT companion's pre-generated asset behind a FAKED generation
 * delay — the "generating…" placeholder holds for a client-side beat, then swaps
 * to the (already-stored) e2 asset, so the free front door feels like a live
 * render (memory `monetization-onboarding-funnel`). Driven purely by the injected
 * {@link RevealScheduler} in {@link createFakedReveal}; it never listens to job
 * status, which is what keeps it distinct from the real WS swap (PR8 F6).
 */
import type { MediaKind } from '@4f/shared';
import { MediaTile, useT } from '@4f/ui';
import { type JSX, onCleanup, onMount, Show } from 'solid-js';
import {
  createFakedReveal,
  DEFAULT_FAKE_DELAY_MS,
  type RevealScheduler,
} from './faked-generation.ts';

export interface FakedMediaProps {
  readonly kind: MediaKind;
  /** The pre-generated asset URL revealed once the faked wait elapses. */
  readonly src: string;
  /** Override the faked wait — tests inject a short/zero delay. */
  readonly delayMs?: number;
  /** Inject the timer seam — tests pump it manually (no real clock). */
  readonly scheduler?: RevealScheduler;
}

export function FakedMedia(props: FakedMediaProps): JSX.Element {
  const { t } = useT();
  const reveal = createFakedReveal(props.scheduler, props.delayMs ?? DEFAULT_FAKE_DELAY_MS);

  onMount(() => reveal.start());
  onCleanup(() => reveal.cancel());

  const generatingAlt = (): string =>
    props.kind === 'video' ? t('surface.generatingVideo') : t('surface.generatingImage');

  return (
    <Show
      when={reveal.phase() === 'revealed'}
      fallback={<MediaTile kind={props.kind} loading alt={generatingAlt()} />}
    >
      <MediaTile kind={props.kind} src={props.src} alt={t('surface.revealedMedia')} />
    </Show>
  );
}
